import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm, useFormState } from 'react-final-form';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { addToast } from 'src/old/actions';
import { useConnectBukMutation, useSaveBukMutation } from 'src/shared/api/integrations/buk';
import { useGetIntegrationDataQuery } from 'src/shared/api/integrations/factorial';
import { useGetCompaniesQuery } from 'src/shared/api/company';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { getIsConnected } from '../utils/getIsConnected';
import { type BukValues, mapFormDataToBukConfig } from './utils';

const integrationName = 'buk';

export const useBukConnection = () => {
  const form = useForm();
  const { data: companies } = useGetCompaniesQuery();
  const [selectedCompany, setSelectedCompany] = useState('');
  const previousSelectedCompany = useRef('');
  const { data: integrationData, isLoading } = useGetIntegrationDataQuery();
  const [triggerConnection] = useConnectBukMutation();
  const { values } = useFormState();

  const isConnected = getIsConnected({
    isLoading,
    integrationData,
    subsidiary: values.subsidiary,
    integrationName
  });

  const company = integrationData?.find(
    item => item.subsidiary === selectedCompany && item.software === integrationName
  );

  const handleSelectedCompanyChange = (newCompany: string) => {
    setSelectedCompany(newCompany);
  };

  useEffect(() => {
    if (!integrationData || !companies || !selectedCompany) {
      return;
    }

    const companyChanged = previousSelectedCompany.current !== selectedCompany;
    previousSelectedCompany.current = selectedCompany;

    if (!company) {
      if (companyChanged) {
        form.change('apiKey', '');
        form.change('urlPrefix', '');
        form.change('country', '');
        form.change('company_id', '');
        form.change('conceptCode', '');
        form.change('recordTransactions', false);
      }
      return;
    }

    if (company.credentials?.apiKey) {
      form.change('apiKey', company.credentials.apiKey);
    } else if (companyChanged) {
      form.change('apiKey', '');
    }

    if (company.credentials?.urlPrefix ?? company.parameters?.urlPrefix) {
      form.change('urlPrefix', company.credentials?.urlPrefix ?? company.parameters?.urlPrefix);
    } else if (companyChanged) {
      form.change('urlPrefix', '');
    }

    if (company.credentials?.country ?? company.parameters?.country) {
      form.change('country', company.credentials?.country ?? company.parameters?.country);
    } else if (companyChanged) {
      form.change('country', '');
    }

    if (company.parameters?.company_id != null) {
      form.change('company_id', company.parameters.company_id);
    } else if (companyChanged) {
      form.change('company_id', '');
    }

    if (company.parameters?.conceptCode != null) {
      form.change('conceptCode', company.parameters.conceptCode);
    } else if (companyChanged) {
      form.change('conceptCode', '');
    }

    form.change('recordTransactions', company.flowsEnabled?.batchPaymentSync ?? false);
  }, [integrationData, form, companies, selectedCompany, company]);

  const isConnectBtnDisabled = useMemo(() => {
    const allFilled = Boolean(values.apiKey && values.urlPrefix && values.country);
    const unchangedCredentials =
      values.apiKey === company?.credentials?.apiKey &&
      values.urlPrefix === (company?.credentials?.urlPrefix ?? company?.parameters?.urlPrefix) &&
      values.country === (company?.credentials?.country ?? company?.parameters?.country);

    return !allFilled || Boolean(isConnected && unchangedCredentials);
  }, [values.apiKey, values.urlPrefix, values.country, company, isConnected]);

  return {
    selectedCompany,
    handleSelectedCompanyChange,
    triggerConnection,
    isConnected,
    isConnectBtnDisabled
  };
};

export const useBukSaveIntegration = () => {
  const [saveConfig] = useSaveBukMutation();
  const dispatch = useDispatch();
  const { t } = useTranslation('superAdmin');

  const saveBukIntegrationData = () => (formData: BukValues) => async () => {
    try {
      const payload = mapFormDataToBukConfig(formData);

      if (!payload) {
        dispatch(addToast({ ...toastTypes.error, defaultMessage: t('toasts.error.invalid data') }));
        return;
      }

      await saveConfig(payload).unwrap();
      dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
    } catch (error) {
      console.error(error);
      const defaultMessage =
        (error as FetchBaseQueryError)?.status === 400 ? t('toasts.error.invalid data') : t('toasts.error');
      dispatch(addToast({ ...toastTypes.error, defaultMessage }));
    }
  };

  return saveBukIntegrationData;
};
